import React, { createContext, useContext, useMemo, useState } from 'react';
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';

import { getItemFromLocalStorage, storeItemToLocalStorage } from '../utils/localStorage';
import { localStorageKeys } from '../utils/enums';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { frFR } from '@mui/x-date-pickers/locales';
import { darkAgGridTheme, darkMuiTheme, lightAgGridTheme, lightMuiTheme, darkAgChartTheme, lightAgChartTheme } from '../themes';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';

export type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
    mode: ThemeMode;
    toggleTheme: () => void;
    setThemeMode: (mode: ThemeMode) => void;
    muiTheme: typeof darkMuiTheme;
    agGridTheme: typeof darkAgGridTheme;
    agChartTheme: typeof darkAgChartTheme;
}

export const ThemeContext = createContext<ThemeContextType>({
    mode: 'dark',
    toggleTheme: () => {},
    setThemeMode: () => {},
    muiTheme: darkMuiTheme,
    agGridTheme: darkAgGridTheme,
    agChartTheme: darkAgChartTheme,
});

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [mode, setMode] = useState<ThemeMode>((getItemFromLocalStorage(localStorageKeys.THEME) as ThemeMode) || 'dark');

    const toggleTheme = () => {
        storeItemToLocalStorage(localStorageKeys.THEME, mode === 'dark' ? 'light' : 'dark');
        setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
    };

    const setThemeMode = (newMode: ThemeMode) => {
        storeItemToLocalStorage(localStorageKeys.THEME, newMode);
        setMode(newMode);
    };

    const muiTheme = useMemo(() => (mode === 'dark' ? darkMuiTheme : lightMuiTheme), [mode]);
    const agGridTheme = useMemo(() => (mode === 'dark' ? darkAgGridTheme : lightAgGridTheme), [mode]);
    const agChartTheme = useMemo(() => (mode === 'dark' ? darkAgChartTheme : lightAgChartTheme), [mode]);

    return (
        <ThemeContext.Provider value={{ mode, toggleTheme, setThemeMode, muiTheme, agGridTheme, agChartTheme }}>
            <MuiThemeProvider theme={muiTheme}>
                <CssBaseline />
                <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="fr"
                    localeText={frFR.components.MuiLocalizationProvider.defaultProps.localeText}
                >
                    {children}
                </LocalizationProvider>
                <ToastContainer position="bottom-right" theme={mode === 'dark' ? 'dark' : 'light'} stacked draggable pauseOnFocusLoss={false} />
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};
